import React, { useEffect, useRef } from "react";
import { Box } from "@mui/material";
import Map from "ol/Map";
import View from "ol/View";
import TileLayer from "ol/layer/Tile";
import { XYZ } from "ol/source";
import { fromLonLat } from "ol/proj";
import { Point, Polygon } from "ol/geom";
import Feature from "ol/Feature";
import { Vector as VectorLayer } from "ol/layer";
import { Vector as VectorSource } from "ol/source";
import { Style, Icon, Fill, Stroke } from "ol/style";
import { defaults as defaultControls, FullScreen } from "ol/control";
import "ol/ol.css";

const TOKEN_PADRAO =
	"pk.eyJ1Ijoicm9idHAiLCJhIjoiY21hdmdoMjNpMDRoeTJub3I3aGZiYndrMyJ9.mLo63CfWf7NqzeXAA9flZw";

export const COR_DO_PRODUTOR = "#00A69A";
export const COR_DA_DISTRIBUIDORA = "#E8590C";

/**
 * Pino no formato do resto do sistema, com contorno branco para destacar sobre
 * a imagem de satélite e uma letra no miolo. A letra existe para o mapa não
 * depender só da cor: com dois pinos na tela, "P" e "D" resolvem na hora.
 *
 * A cor entra crua ("#RRGGBB"): quem escapa é o encodeURIComponent do data URI.
 * Passar "%23..." aqui vira "%2523" e o navegador cai no preto padrão.
 */
const pino = (cor, letra) =>
	new Style({
		image: new Icon({
			src:
				"data:image/svg+xml;charset=utf-8," +
				encodeURIComponent(`
					<svg xmlns="http://www.w3.org/2000/svg" width="36" height="52" viewBox="-2 -2 36 52">
						<path fill="${cor}" stroke="#FFFFFF" stroke-width="2" d="M16 0C7.2 0 0 7.2 0 16c0 12 16 32 16 32s16-20 16-32c0-8.8-7.2-16-16-16zm0 22c-3.3 0-6-2.7-6-6s2.7-6 6-6 6 2.7 6 6-2.7 6-6 6z"/>
						<circle cx="16" cy="16" r="6" fill="#FFFFFF"/>
						<text x="16" y="19.5" text-anchor="middle" font-family="Arial, Helvetica, sans-serif" font-size="10" font-weight="bold" fill="${cor}">${letra}</text>
					</svg>
				`),
			scale: 1,
			// A ponta do pino fica em y=48 de um viewBox que vai de -2 a 50.
			anchor: [0.5, 50 / 52],
		}),
	});

/** A área desenhada é a propriedade do produtor, então segue a cor dele. */
const estiloDoPoligono = new Style({
	fill: new Fill({ color: "rgba(0, 166, 154, 0.25)" }),
	stroke: new Stroke({ color: COR_DO_PRODUTOR, width: 3 }),
});

/**
 * Mapa da origem do produto, equivalente ao da seção de rastreabilidade do
 * portal do campo. Desenha o polígono da propriedade do produtor quando ele
 * existe, mais um pino no produtor e outro na distribuidora que beneficiou.
 *
 * Só aparece se houver alguma coordenada: cadastro sem geolocalização não
 * deve virar um mapa vazio no meio do oceano.
 */
function MapaOrigem({ produtores = [], distribuidora }) {
	const elementoDoMapa = useRef(null);

	useEffect(() => {
		if (!elementoDoMapa.current) return;

		const feicoes = [];
		const pontosParaEnquadrar = [];

		for (const produtor of produtores) {
			if (produtor.poligono?.length >= 3) {
				const anel = produtor.poligono.map((ponto) =>
					fromLonLat([ponto.longitude, ponto.latitude]),
				);
				// O polígono precisa fechar no primeiro ponto.
				anel.push(anel[0]);
				const area = new Feature({ geometry: new Polygon([anel]) });
				area.setStyle(estiloDoPoligono);
				feicoes.push(area);
				pontosParaEnquadrar.push(...anel);
			}

			if (produtor.latitude != null && produtor.longitude != null) {
				const ponto = fromLonLat([produtor.longitude, produtor.latitude]);
				const marcador = new Feature({ geometry: new Point(ponto) });
				marcador.setStyle(pino(COR_DO_PRODUTOR, "P"));
				feicoes.push(marcador);
				pontosParaEnquadrar.push(ponto);
			}
		}

		if (distribuidora?.latitude != null && distribuidora?.longitude != null) {
			const ponto = fromLonLat([distribuidora.longitude, distribuidora.latitude]);
			const marcador = new Feature({ geometry: new Point(ponto) });
			marcador.setStyle(pino(COR_DA_DISTRIBUIDORA, "D"));
			feicoes.push(marcador);
			pontosParaEnquadrar.push(ponto);
		}

		if (feicoes.length === 0) return;

		const token = process.env.REACT_APP_MAPBOX_TOKEN || TOKEN_PADRAO;
		const camadaDeFeicoes = new VectorLayer({
			source: new VectorSource({ features: feicoes }),
		});

		const mapa = new Map({
			target: elementoDoMapa.current,
			controls: defaultControls().extend([new FullScreen()]),
			layers: [
				new TileLayer({
					source: new XYZ({
						url: `https://api.mapbox.com/styles/v1/mapbox/satellite-streets-v12/tiles/{z}/{x}/{y}@2x?access_token=${token}`,
						tilePixelRatio: 2,
						maxZoom: 19,
					}),
				}),
				camadaDeFeicoes,
			],
			view: new View({ center: pontosParaEnquadrar[0], zoom: 15 }),
		});

		// Enquadra tudo que foi desenhado; com um ponto só o fit zera o zoom.
		if (pontosParaEnquadrar.length > 1) {
			mapa.getView().fit(camadaDeFeicoes.getSource().getExtent(), {
				padding: [40, 40, 40, 40],
				maxZoom: 16,
			});
		}

		const ajuste = setTimeout(() => mapa.updateSize(), 100);

		return () => {
			clearTimeout(ajuste);
			mapa.setTarget(null);
		};
	}, [produtores, distribuidora]);

	const temCoordenada =
		produtores.some(
			(p) => p.latitude != null || p.poligono?.length >= 3,
		) ||
		(distribuidora?.latitude != null && distribuidora?.longitude != null);

	if (!temCoordenada) return null;

	return (
		<Box
			ref={elementoDoMapa}
			sx={{
				width: "100%",
				height: { xs: "320px", md: "70vh" },
				borderRadius: 2,
				overflow: "hidden",
				boxShadow: "-7px 10px 43px -15px rgba(0,0,0,0.75)",
			}}
		/>
	);
}

export default MapaOrigem;
